import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { useAuth } from "@/hooks/use-auth.tsx";
import { useValidationError } from "@/hooks/use-validation-errors.ts";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/email/verify")({
	validateSearch: z.object({
		verify_url: z.string(),
	}),
	loaderDeps: ({ search }) => ({
		verify_url: search.verify_url,
	}),
	component: Page,
});

function Page() {
	const { verifyEmail } = useAuth();
	const navigate = useNavigate({ from: "/email/verify" });
	const search = Route.useSearch();
	const [loading, setLoading] = useState(true);
	const [_, setMessage] = useState("");
	const [alertType, setAlertType] = useState<"success" | "error" | "info">(
		"info",
	);
	const [errors, setErrors] = useValidationError();

	useEffect(() => {
		const verifyUrl = search.verify_url;

		if (!verifyUrl) {
			setMessage("Missing verification URL.");
			setAlertType("error");
			setLoading(false);
			return;
		}

		async function verify() {
			try {
				await verifyEmail({ verify_url: verifyUrl });
				setMessage("Email verified successfully.");
				setAlertType("success");
			} catch (e) {
				if (e instanceof AxiosError) {
					setErrors(e.response?.data.errors);
					setMessage(e.response?.data.message || "Verification failed.");
					setAlertType("error");
				} else {
					setMessage("Verification failed.");
					setAlertType("error");
				}
			} finally {
				setLoading(false);
			}
		}

		verify();
	}, [search.verify_url]);

	return (
		<div className="flex flex-col items-center justify-center h-screen p-4 bg-background">
			<div className="max-w-[420px] w-full">
				{loading ? (
					<Alert type="info">Verifying your email, please wait...</Alert>
				) : (
					<Alert type={alertType}>
						{errors || "Your email has been verified"}
					</Alert>
				)}
				<div className="mt-4">
					<Button onClick={() => navigate({ to: "/login" })} className="w-full">
						Go to Login
					</Button>
				</div>
			</div>
		</div>
	);
}

export default Page;
